import {
	ChangeDetectionStrategy,
	Component,
	computed,
	EventEmitter,
	inject,
	Input,
	Output,
	signal,
	WritableSignal,
	DOCUMENT,
} from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { MatRippleModule } from '@angular/material/core';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { IChecklistOption, IChecklistTemplate } from '@app/domain/types';

const emptyChecklistOption: IChecklistOption = {
	question: '',
	answer: null,
	reason: '',
	comment: '',
};

@Component({
	selector: 'checklist-options',
	imports: [
		MatButtonModule,
		MatIconModule,
		FormsModule,
		MatDialogModule,
		MatRippleModule,
		NgxTolgeeModule,
	],
	templateUrl: './checklist-options.component.html',
	styleUrls: ['./checklist-options.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChecklistOptionsComponent {
	private readonly document = inject(DOCUMENT);

	@Input() showActionButtons: boolean = false;
	@Input() set checklistTemplate(checklistTemplate: IChecklistTemplate) {
		if (!checklistTemplate) {
			return;
		}
		this.checklistOptions.set(structuredClone(checklistTemplate.options));
	}
	@Output() checklistOptionsChanged = new EventEmitter<string>();
	@Output() cancelPressed = new EventEmitter<void>();
	@Output() savePressed = new EventEmitter<string>();

	checklistOptions: WritableSignal<IChecklistOption[]> = signal([
		structuredClone(emptyChecklistOption),
	]);
	optionsCount = computed(() => this.checklistOptions().length);
	onlyFilledOptions = computed(() => {
		const options = this.checklistOptions();
		return options.filter(option => {
			return option.question.length > 0;
		});
	});

	addNewOption(evt: KeyboardEvent | MouseEvent): void {
		evt.preventDefault();
		this.checklistOptions.update(options => [
			...options,
			structuredClone(emptyChecklistOption),
		]);
		this.focusNewOption();
	}

	deleteOption(index: number): void {
		const newOptions = [...this.checklistOptions()];
		newOptions.splice(index, 1);
		this.checklistOptions.set(newOptions);
		this.emitChanges();
	}

	optionChanged(evt: string, index: number): void {
		this.checklistOptions.update(options => {
			options[index].question = evt.trim();
			return [...options];
		});
		this.emitChanges();
	}

	cancelAction() {
		this.cancelPressed.emit();
	}

	saveAction() {
		this.savePressed.emit(this.prepareOptionsTemplate());
	}

	private emitChanges(): void {
		this.checklistOptionsChanged.emit(this.prepareOptionsTemplate());
	}

	private prepareOptionsTemplate(): string {
		const options = this.onlyFilledOptions();
		const count = options.length;

		if (count === 0) {
			return '';
		}

		return JSON.stringify({
			options: options,
			overallCount: count,
		});
	}

	private focusNewOption(): void {
		setTimeout(() => {
			const inputsList: NodeListOf<HTMLInputElement> = this.document.querySelectorAll(
				'.checklist-option-input input'
			);
			const newOption: HTMLInputElement = Array.from(inputsList).pop();
			if (newOption) {
				newOption.scrollIntoView({ behavior: 'smooth' });
				newOption.focus();
			}
		});
	}
}
